<template>
  <div class="sidebar-layout">
    <!-- Sidebar -->
    <div class="sidebar">
      <NavBarAcademy />
    </div>

    <!-- Contenido principal -->
    <div class="content">
      <!-- loading ... -->
      <div v-if="isLoadingBanner" class="banner-skeleton">
        <div class="skeleton-banner">
          <div class="skeleton-overlay">
            <div class="skeleton-title"></div>
            <div class="skeleton-description"></div>
            <div class="skeleton-meta"></div>
          </div>
        </div>
      </div>

      <!-- BANNER POR DEFECTO -->
      <AcademyBanner 
        v-else-if="!customBanner && !isLoadingBanner" 
      /> 
      
      <div v-else-if="customBanner && !isLoadingBanner" class="banner-container">
        <div v-if="isLoadingBannerImage" class="banner-skeleton">
          <div class="skeleton-banner loading-image">
            <div class="skeleton-overlay">
              <div class="skeleton-title"></div>
              <div class="skeleton-description"></div>
              <div class="skeleton-meta"></div>
            </div>
          </div>
        </div>
        <CourseBanner 
          v-show="!isLoadingBannerImage"
          :banner-data="customBanner"
          :can-edit="canEditBanner"
          @edit-banner="openBannerEditor"
          @title-changed="handleTitleChange"
          @banner-image-loaded="handleBannerImageLoaded"
          @banner-image-error="handleBannerImageError"
        />
      </div>
      
      <!-- MODAL PARA EDITAR BANNER -->
      <b-modal 
        id="bannerEditorModal" 
        :title="$t('academy.edit_banner')" 
        size="lg"
        @ok="saveBannerChanges"
        :ok-disabled="isSaving">
        
        <div v-if="bannerEditData">
          <!-- Vista previa del banner actual -->
          <div class="current-banner-preview mb-4">
            <h6>{{ $t('academy.preview') }}:</h6>
            
            <div v-if="isLoadingPreviewImage" class="preview-skeleton">
              <div class="skeleton-preview">
                <div class="skeleton-overlay">
                  <div class="skeleton-title"></div>
                  <div class="skeleton-description"></div>
                  <div class="skeleton-meta"></div>
                </div>
              </div>
            </div>
            
            <!-- Preview real -->
            <div v-else class="banner-preview" :style="previewBannerStyles">
              <div class="banner-overlay-preview">
                <div class="banner-content-preview">
                  <div class="course-info-preview">
                    <h3 class="course-title">{{ bannerEditData.courseName }}</h3>
                    <p class="course-description">{{ bannerEditData.courseDescription }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Campo para subir nueva imagen -->
          <b-form-group :label="$t('academy.banner_image')">
            <b-form-file
              v-model="newBannerFile"
              accept="image/*"
              :placeholder="$t('academy.select_new_banner') + '...'"
              @change="handleBannerFileChange"
            />
            <small class="form-text text-muted">
              {{ $t('academy.banner_recommendation') }}
            </small>
          </b-form-group>

          <!-- Loading durante la subida -->
          <div v-if="isUploading" class="text-center">
            <b-spinner small></b-spinner>
            <span class="ml-2">{{ $t('academy.uploading_image') }}</span>
          </div>
        </div>
      </b-modal>
      
      <slot></slot>
    </div>
  </div>
</template>
<script>
import NavBarAcademy from "@/components/academy/NavBarAcademy.vue";
import AcademyBanner from "@/components/academy/AcademyBanner.vue";
import CourseBanner from "@/components/academy/CourseBanner.vue";
import coursesDataTest from '@/views/TestEducationalInstitutions/coursesDataTest.js';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

export default {
  name: 'AcademyLayout',
  components: {
    NavBarAcademy,
    AcademyBanner,
    CourseBanner
  },
  props: {
    customBanner: {
      type: Object,
      default: null
    },
    canEditBanner: {
      type: Boolean,
      default: false
    },
    currentCourseId: {
      type: String,
      default: null
    },
    isLoadingBanner: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      bannerEditData: null,
      newBannerFile: null,
      isUploading: false,
      isSaving: false,
      previewImageUrl: null,
      isLoadingBannerImage: false,
      isLoadingPreviewImage: false
    };
  },

  watch: {
    'customBanner.bannerImage': {
        handler(newImageUrl, oldImageUrl) {
            console.log(' Imagen del banner:', { newImageUrl, oldImageUrl });
            
            // Solo verificar carga si la URL realmente cambió
            if (newImageUrl && newImageUrl !== oldImageUrl) {
                console.log('Nueva imagen detectada');
                this.checkBannerImageLoad(newImageUrl);
            } else {
                console.log(' Sin cambio de imagen');
                this.isLoadingBannerImage = false;
            }
        },
        immediate: true
    }
},

  computed: {
    previewBannerStyles() {
      if (!this.bannerEditData) return {};
      
      const imageUrl = this.previewImageUrl || this.bannerEditData.bannerImage;
      
      return {
        backgroundImage: imageUrl 
          ? `url(${imageUrl})` 
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        backgroundColor: this.bannerEditData.backgroundColor || '#667eea'
      };
    }
  },

  methods: {

    async handleTitleChange(data) {
      try {
        console.log('Manejando cambio de título:', data);
        
        // Actualizar en la base de datos
        await coursesDataTest.updateCourseFields(data.courseId, {
          name: data.newTitle
        });

        console.log('Título actualizado en BD');

        this.$emit('course-title-updated', {
          courseId: data.courseId,
          newTitle: data.newTitle,
          oldTitle: data.oldTitle
        });

        // Mostrar notificación de éxito
        this.$bvToast.toast(this.$t('academy.name_course_updated_success'), {
          title: this.$t('academy.success'),
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        });

      } catch (error) {
        console.error('Error al actualizar título del curso:', error);
        
        this.$bvToast.toast(this.$t('academy.name_course_updated_error'), {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
      }
    },
    openBannerEditor() {
      this.bannerEditData = { ...this.customBanner };
      this.newBannerFile = null;
      this.previewImageUrl = null;
      this.isLoadingPreviewImage = false;
      this.$bvModal.show('bannerEditorModal');
    },

    handleBannerFileChange(event) {
      const file = event.target.files[0];
      if (file) {

        this.isLoadingPreviewImage = true;
        
        const reader = new FileReader();
        reader.onload = (e) => {
          this.previewImageUrl = e.target.result;

          this.isLoadingPreviewImage = false;
        };
        reader.onerror = () => {
          this.isLoadingPreviewImage = false;
        };
        reader.readAsDataURL(file);
      }
    },


    checkBannerImageLoad(imageUrl) {
      if (!imageUrl) {
        this.isLoadingBannerImage = false;
        return;
      }

      this.isLoadingBannerImage = true;
      
      const img = new Image();
      
      img.onload = () => {
        console.log('Imagen del banner cargada:', imageUrl);
        this.isLoadingBannerImage = false;
      };
      
      img.onerror = () => {
        console.error('Error al cargar imagen del banner:', imageUrl);
        this.isLoadingBannerImage = false;
      };

      // Iniciar carga de la imagen
      img.src = imageUrl;
    },

    handleBannerImageLoaded() {
      console.log('Imagen del banner completamente cargada');
      this.isLoadingBannerImage = false;
    },

    handleBannerImageError() {
      console.error(' Error al cargar imagen del banner');
      this.isLoadingBannerImage = false;
    },

    updatePreview() {
      this.$forceUpdate();
    },

    async saveBannerChanges(bvModalEvt) {
      bvModalEvt.preventDefault();
      
      // Validaciones existentes...
      if (!this.bannerEditData.courseName || !this.bannerEditData.courseName.trim()) {
        console.warn('El nombre del curso es requerido');
        return;
      }

      if (this.bannerEditData.courseName.trim().length < 3) {
        console.warn('El nombre del curso debe tener al menos 3 caracteres');
        return;
      }
        
      try {
        this.isSaving = true;
        let bannerImageUrl = this.bannerEditData.bannerImage;

        // ✅ SUBIR NUEVA IMAGEN A FIREBASE STORAGE
        if (this.newBannerFile) {
          this.isUploading = true;
          
          console.log(' Subiendo imagen del banner a Firebase Storage...');
          
          // Crear referencia en Storage
          const storage = getStorage();
          const timestamp = Date.now();
          const fileExtension = this.newBannerFile.name.split('.').pop().toLowerCase();
          const fileName = `courses/${this.bannerEditData.courseId}/banner/banner-${timestamp}.${fileExtension}`;
          
          const storageRef = ref(storage, fileName);
          
          // Subir archivo
          const snapshot = await uploadBytes(storageRef, this.newBannerFile);
          
          // Obtener URL de descarga
          bannerImageUrl = await getDownloadURL(snapshot.ref);
          
          console.log('Imagen del banner subida exitosamente:', bannerImageUrl);
        }

        const courseId = this.bannerEditData.courseId;
        
        console.log('Guardando cambios del curso:', {
          courseId,
          courseName: this.bannerEditData.courseName,
          hasNewImage: !!this.newBannerFile,
          imageUrl: bannerImageUrl ? 'URL presente' : 'Sin imagen'
        });
        
        await coursesDataTest.updateCourseFields(courseId, {
          // Información del curso
          name: this.bannerEditData.courseName.trim(),
          description: this.bannerEditData.courseDescription?.trim() || '',
          
          imageBanner: bannerImageUrl,
          backgroundColor: this.bannerEditData.backgroundColor || '#667eea'
        });

        if (this.newBannerFile) {
          this.isLoadingBannerImage = true;
        }

        // Emitir datos actualizados
        this.$emit('banner-updated', {
          ...this.bannerEditData,
          courseName: this.bannerEditData.courseName.trim(),
          courseDescription: this.bannerEditData.courseDescription?.trim() || '',
          bannerImage: bannerImageUrl
        });

        this.$nextTick(() => {
          this.$bvModal.hide('bannerEditorModal');
        });

        console.log('Banner y curso actualizados correctamente');

      } catch (error) {
        console.error('Error al guardar cambios:', error);
      } finally {
        this.isUploading = false;
        this.isSaving = false;
      }
    }
  }
};
</script>

<style scoped>
.banner-container {
  position: relative;
}

.skeleton-banner.loading-image {
  background: linear-gradient(90deg, #f0f0f0 25%, #e6e6e6 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
}

.preview-skeleton {
  height: 340px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e6e6e6 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 30px;
  position: relative;
  overflow: hidden;
  border: 2px solid #dee2e6;
}

.skeleton-preview {
  height: 100%;
  position: relative;
}

/* ✅ SKELETON OVERLAY PURO (sin spinner) */
.skeleton-preview .skeleton-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 20px;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0.7;
  }
}

.course-description {
  font-size: 1.1rem;
  margin-bottom: 15px;
  opacity: 0.9;
  line-height: 1.4;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
}

.course-title {
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 10px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.7);
}

.sidebar-layout {
  display: flex;
  height: 100%;
}

.sidebar {
  flex: 0 0 auto;
  position: sticky;
  top: 0;
  height: 100vh;
}

.content {
  flex-grow: 1;
  padding-left: 30px;
  padding-right: 15px;
  padding-top: 15px;
  padding-bottom: 15px;
}

.banner-skeleton {
  margin-bottom: 20px;
}

.skeleton-banner {
  height: 330px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e6e6e6 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 30px;
  position: relative;
  overflow: hidden;
}

.skeleton-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 20px;
}

.skeleton-title {
  width: 60%;
  height: 40px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  margin-bottom: 10px;
  animation: shimmer 2s infinite;
}

.skeleton-description {
  width: 80%;
  height: 20px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  margin-bottom: 15px;
  animation: shimmer 2s infinite;
}

.skeleton-meta {
  display: flex;
  gap: 10px;
}

.skeleton-meta::before,
.skeleton-meta::after {
  content: '';
  width: 80px;
  height: 25px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  animation: shimmer 2s infinite;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@keyframes shimmer {
  0% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.6;
  }
  100% {
    opacity: 0.3;
  }
}

.banner-preview {
  height: 340px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 30px;
  position: relative;
  overflow: hidden;
  border: 2px solid #dee2e6;
}

.banner-overlay-preview {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  padding: 15px;
}

.banner-content-preview {
  color: white;
  width: 100%;
}

</style>